import {
  type PreparedShape,
  type Rng,
  type ShapeDefinition,
  type ShapeSink,
  type Vec3,
} from '../shape.js';
import { alongSegment, hash01, inBox, onDisc, onSphere, revolve, TAU } from './helpers.js';

/* ── Vinyl ─────────────────────────────────────────────────────────────── */

const PLATTER_RADIUS = 5.6;
const ARM_PIVOT: Vec3 = { x: 6.4, y: 1.1, z: -3.6 };
const ARM_NEEDLE: Vec3 = { x: 1.6, y: 0.42, z: 1.4 };

export const vinyl: ShapeDefinition = {
  name: 'vinyl',
  palette: { base: 0x36_30_3e, shade: 0x8a_7c_98, accent: 0xff_c3_5c },
  driftRate: 1.6,
  framing: 2.7,
  prepare: ({ random, total, variant }): PreparedShape => {
    const grooveScale = 7 + variant * 6;
    const place = (index: number, sink: ShapeSink): void => {
      const p = index / total;
      if (p < 0.52) {
        // The record itself: grooves as concentric brightness rings.
        onDisc(sink, random, PLATTER_RADIUS);
        const r = Math.hypot(sink.x, sink.z);
        sink.y = 0.42;
        const groove = Math.abs(Math.sin(r * grooveScale));
        sink.tint = r < 1.75 ? 0.86 + random() * 0.14 : 0.12 + groove * 0.34;
        // A tangential nudge, so the disc always looks like it is turning.
        sink.driftX = -sink.z * 0.06;
        sink.driftZ = sink.x * 0.06;
        sink.phase = Math.atan2(sink.z, sink.x);
      } else if (p < 0.62) {
        // Spindle.
        revolve(sink, random, () => 0.09, 1);
        sink.y += 0.4;
        sink.tint = 0.7;
      } else if (p < 0.74) {
        // Tone arm, from pivot to needle, with a counterweight.
        if (random() < 0.82) {
          alongSegment(sink, random, ARM_PIVOT, ARM_NEEDLE, 0.055);
          sink.tint = 0.62 + random() * 0.16;
        } else {
          onSphere(sink, random, 0.36);
          sink.x += ARM_PIVOT.x + 0.6;
          sink.y += ARM_PIVOT.y;
          sink.z += ARM_PIVOT.z - 0.45;
          sink.tint = 0.5;
        }
      } else if (p < 0.9) {
        // Plinth.
        inBox(sink, random, { x: 7.6, y: 0.22, z: 6.6 });
        sink.y += 0.1;
        sink.tint = 0.08 + random() * 0.1;
      } else {
        // Sound rising off the groove.
        const angle = random() * TAU;
        const r = 1.8 + random() * (PLATTER_RADIUS - 1.8);
        const rise = random();
        sink.x = Math.cos(angle) * r;
        sink.z = Math.sin(angle) * r;
        sink.y = 0.5 + rise * 7;
        sink.tint = 0.9 + random() * 0.1;
        sink.driftY = 0.7;
        sink.driftX = 0.3;
        sink.phase = hash01(index) * TAU;
      }
    };
    return { place };
  },
};

/* ── Piano ─────────────────────────────────────────────────────────────── */

const WHITE_KEYS = 52;
const KEY_WIDTH = 0.31;
const KEYBOARD_HALF = (WHITE_KEYS * KEY_WIDTH) / 2;
const BLACK_AFTER = new Set([0, 1, 3, 4, 5]);

function whiteKey(sink: ShapeSink, random: Rng, pressed: number): void {
  const key = Math.floor(random() * WHITE_KEYS);
  const dip = Math.abs(key - pressed) < 0.5 ? -0.06 : 0;
  inBox(sink, random, { x: KEY_WIDTH * 0.42, y: 0.055, z: 0.95 });
  sink.x += (key - WHITE_KEYS / 2 + 0.5) * KEY_WIDTH;
  sink.y += 1.3 + dip;
  sink.tint = 0.86 + random() * 0.14;
}

function blackKey(sink: ShapeSink, random: Rng): void {
  let key = Math.floor(random() * (WHITE_KEYS - 1));
  while (!BLACK_AFTER.has(key % 7)) {
    key = (key + 1) % (WHITE_KEYS - 1);
  }
  inBox(sink, random, { x: KEY_WIDTH * 0.26, y: 0.06, z: 0.58 });
  sink.x += (key - WHITE_KEYS / 2 + 1) * KEY_WIDTH;
  sink.y += 1.45;
  sink.z -= 0.34;
  sink.tint = 0.02 + random() * 0.06;
}

export const piano: ShapeDefinition = {
  name: 'piano',
  palette: { base: 0x2e_2a_38, shade: 0x9a_84_68, accent: 0xff_f4_e0 },
  driftRate: 1.4,
  framing: 1.96,
  prepare: ({ random, total, variant }): PreparedShape => {
    const pressed = Math.floor(variant * WHITE_KEYS);
    const place = (index: number, sink: ShapeSink): void => {
      const p = index / total;
      if (p < 0.34) {
        whiteKey(sink, random, pressed);
      } else if (p < 0.48) {
        blackKey(sink, random);
      } else if (p < 0.66) {
        // Case and fallboard behind the keys.
        inBox(sink, random, { x: KEYBOARD_HALF + 0.4, y: 0.75, z: 0.9 });
        sink.y += 0.7;
        sink.z -= 1.9;
        sink.tint = 0.2 + random() * 0.14;
      } else if (p < 0.8) {
        // The lid, propped open above the strings.
        const u = random() * 2 - 1;
        const v = random();
        sink.x = u * (KEYBOARD_HALF + 0.4);
        sink.z = -2.7 - v * 3.4;
        sink.y = 1.9 + v * 3.1;
        sink.tint = 0.3 + v * 0.2;
      } else if (p < 0.9) {
        // Strings running away under the lid.
        const string = Math.floor(random() * 88);
        sink.x = (string / 88 - 0.5) * (KEYBOARD_HALF * 2);
        sink.y = 1.75;
        sink.z = -2.4 - random() * (1.5 + (1 - string / 88) * 3);
        sink.tint = 0.68 + random() * 0.18;
        sink.driftY = 0.05;
        sink.phase = string * 0.7;
      } else {
        // Notes lifting off the keyboard.
        sink.x = (random() * 2 - 1) * KEYBOARD_HALF;
        sink.y = 2 + random() * 7;
        sink.z = random() * 3;
        sink.tint = 0.95 + random() * 0.05;
        sink.driftY = 0.8;
        sink.driftX = 0.4;
        sink.phase = hash01(index) * TAU;
      }
    };
    return { place };
  },
};
